import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useGateApi } from "./Api/useGateApi";
import { AuthGateApiClient, AuthResponse, MeResponse } from "./Api/ApiServerHooks";
import { BrandingGateApiClient, IBrandingDTO } from "./Api/BrandingGateApiClient";
import { useTranslation } from "react-i18next";

function getSafeBrandAsset(url?: string | null): string | undefined {
    if (!url) {
        return undefined;
    }

    const trimmed = url.trim();
    if (!trimmed) {
        return undefined;
    }

    const isSafe = /^(https?:\/\/|\/|data:image\/)/i.test(trimmed) && !/^javascript:/i.test(trimmed);
    return isSafe ? trimmed : undefined;
}

interface GateMainContextProps {
    isAuthed: boolean;
    logo: string;
    token: string | null;
    refreshToken: string | null;
    expires: Date | null;
    login: (data: AuthResponse) => void;
    userData: MeResponse | null;
    // Branding currently shown to the user: the authenticated tenant's branding once logged
    // in, otherwise the pre-login branding resolved from a /login/:slug URL (if any).
    branding: IBrandingDTO | undefined;
    // Branding resolved from a branded login URL before authentication. Set by LoginScene.
    preLoginBranding: IBrandingDTO | null;
    setPreLoginBranding: (branding: IBrandingDTO | null) => void;
    refreshUserData: () => void;
    refreshBranding: () => void;
}

const GateMainContext = createContext<GateMainContextProps | undefined>(undefined);

export const GateMainProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [defaultLogo, setDefaultLogo] = useState<string>("");
    const [isAuthed, setIsAuthed] = useState<boolean>(false);
    const [token, setToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [expires, setExpires] = useState<Date | null>(null);
    const [userData, setUserData] = useState<MeResponse | null>(null);
    const [branding, setBranding] = useState<IBrandingDTO | undefined>(undefined);
    const [preLoginBranding, setPreLoginBranding] = useState<IBrandingDTO | null>(null);
    const { unauthorizedApi, Authorized } = useGateApi(AuthGateApiClient);
    // Hand-written client (not NSwag) so the authenticated branding keeps urlSlug, which the
    // generated TenantBrandingDTO strips out during deserialization.
    const brandingApi = useGateApi(BrandingGateApiClient);

    const { i18n } = useTranslation();

    useEffect(() => {
        if (defaultLogo !== "") {
            return;
        }

        unauthorizedApi.appLogo()
            .then((response) => setDefaultLogo(response.base64 ?? ''))
            .catch(() => {
                setDefaultLogo('');
            });
    }, [unauthorizedApi, defaultLogo]);

    // Authenticated branding wins; otherwise fall back to branding resolved from a branded
    // login URL so the correct theme/logo shows before the user signs in.
    const effectiveBranding = useMemo<IBrandingDTO | undefined>(
        () => branding ?? preLoginBranding ?? undefined,
        [branding, preLoginBranding]
    );

    const logo = useMemo(
        () => getSafeBrandAsset(effectiveBranding?.logoUrl) ?? getSafeBrandAsset(effectiveBranding?.logoMarkUrl) ?? defaultLogo,
        [effectiveBranding?.logoMarkUrl, effectiveBranding?.logoUrl, defaultLogo]
    );

    const loadCurrentUser = useCallback(() => {
        // The session this call belongs to. A login finishing in parallel (a tool login coming
        // back through /auth-callback) can replace it while the request is in flight; the
        // outcome of this one is then stale and must not sign the new session out.
        const requestedWith = localStorage.getItem('token');
        Authorized()?.me().then((me) => {
            if (localStorage.getItem('token') !== requestedWith) return;

            setUserData(me);
            i18n.changeLanguage(me.language || 'cs');
        }).catch(() => {
            if (localStorage.getItem('token') !== requestedWith) return;

            setUserData(null);
            setBranding(undefined);
            setIsAuthed(false);
        });
    }, [Authorized, i18n]);

    const refreshBranding = useCallback(() => {
        if (!localStorage.getItem('token')) {
            setBranding(undefined);
            return;
        }

        const requestedWith = localStorage.getItem('token');
        brandingApi.authorizedApi?.getTenantBranding()
            .then((response) => {
                if (localStorage.getItem('token') !== requestedWith) return;

                setBranding(response.branding);
            })
            .catch(() => {
                if (localStorage.getItem('token') !== requestedWith) return;

                setBranding(undefined);
            });
    }, [brandingApi.authorizedApi]);

    const login = (data: AuthResponse) => {
        setToken(data.token ?? null);
        setRefreshToken(data.refreshToken ?? null);
        if (data.tokenExpiration) {
            setExpires(new Date(data.tokenExpiration));
        }
        setIsAuthed(true);
        loadCurrentUser();
        refreshBranding();
    }

    useEffect(() => {
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refresh_token');
        const expires = localStorage.getItem('token_expires');
        if (token && refreshToken && expires) {
            setToken(token);
            setRefreshToken(refreshToken);
            setExpires(new Date(expires));
            setIsAuthed(true);
        }
    }, []);

    const refreshUserData = useCallback(() => {
        if (!localStorage.getItem('token')) {
            return;
        }

        loadCurrentUser();
    }, [loadCurrentUser]);

    useEffect(() => {
        if (!isAuthed) {
            setBranding(undefined);
            setUserData(null);
            return;
        }

        loadCurrentUser();
    }, [isAuthed, loadCurrentUser]);

    useEffect(() => {
        if (!isAuthed) {
            return;
        }

        refreshBranding();
    }, [isAuthed, refreshBranding, userData?.tenantId]);

    const contextValue = useMemo<GateMainContextProps>(() => ({
        logo, isAuthed, token, refreshToken, expires, login, userData,
        branding: effectiveBranding, preLoginBranding, setPreLoginBranding,
        refreshUserData, refreshBranding
    }), [logo, isAuthed, token, refreshToken, expires, login, userData,
        effectiveBranding, preLoginBranding, refreshUserData, refreshBranding]);

    return (
        <GateMainContext.Provider value={contextValue}>
            {children}
        </GateMainContext.Provider>
    );
};

export const useGateMainContext = () => {
    const context = useContext(GateMainContext);
    if (context === undefined) {
        throw new Error('GateMainContext must be used within a GateMainProvider');
    }
    return context;
};